import React from 'react';
import { getStaticViewPixel } from '@sparefoot/react-core/utils/tracking';
import { Page, Section, Container, Group } from 'components/core/Page';
import { Link } from 'components/core/Link';
import { H1, Paragraph } from 'components/core/Typography';

import './LegalPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import usePageViewTracking from 'hooks/usePageViewTracking';
import useCanonical from 'hooks/useCanonical';

export function LegalPage() {
	const canonical = useCanonical('/legal/consumer/');
	usePageViewTracking(PAGE_TYPES.LEGAL);
	return (
		<Page
			className="legal-page"
			trackingPixel={getStaticViewPixel()}
			pageType={PAGE_TYPES.LEGAL}
			title="Consumer Terms of Service | SelfStorage.com"
			canonical={canonical}
		>
			<Section>
				<Container>
					<Group>
						<H1 size="large">
							Selfstorage.com Consumer Terms of Service
						</H1>
					</Group>
					<Group>
						<Paragraph>
							Effective for all Users as of October 22, 2025
						</Paragraph>
						<Paragraph weight="bold">
							1. ACCEPTANCE OF TERMS
						</Paragraph>
						<Paragraph>
							Selfstorage.com (“Selfstorage.com,” “we,” “our,” or
							“us”) is happy to provide you with its
							technology-enabled services, including a reservation
							service designed to assist you in determining the
							availability of third-party self-storage units
							("Units") and initiating reservations for specific
							Units or other third-party services
							("Reservations"), the website and associated domains
							of https://www.Selfstorage.com (the "Website"), and
							other related software, content, and services,
							including all versions and upgrades thereto
							(collectively, the “Services”). Your use of the
							Services is subject to and governed by the terms and
							conditions in this Terms of Service (“TOS”).
							Selfstorage.com may, at its discretion, update this
							TOS at any time. You can access and review the most
							current version of this TOS at the URL for this page
							or by clicking on the “Terms of Service” link within
							the Services, or as otherwise made available by
							Selfstorage.com.
						</Paragraph>
						<Paragraph>
							PLEASE REVIEW THIS TOS CAREFULLY. BY REGISTERING FOR
							AN ACCOUNT OR OTHERWISE ACCESSING OR USING THE
							SERVICES, YOU AGREE TO BE BOUND BY THIS TOS,
							INCLUDING ANY UPDATES OR REVISIONS POSTED HERE OR
							OTHERWISE COMMUNICATED TO YOU. IF YOU DO NOT AGREE
							WITH THE TERMS AND CONDITIONS OF THIS TOS, YOU MAY
							NOT ACCESS OR USE THE SERVICES.
						</Paragraph>
						<Paragraph>
							THIS TOS REQUIRES FINAL AND BINDING ARBITRATION TO
							RESOLVE ANY DISPUTE OR CLAIM ARISING OUT OF OR
							RELATING IN ANY WAY TO THIS TOS, OR YOUR ACCESS TO
							OR USE OF THE SERVICES, INCLUDING THE VALIDITY,
							APPLICABILITY OR INTERPRETATION OF THIS TOS, AND YOU
							AGREE THAT ANY SUCH CLAIM WILL BE RESOLVED ONLY ON
							AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED
							OR REPRESENTATIVE ACTION, ARBITRATION OR OTHER
							SIMILAR PROCESS. PLEASE REVIEW SECTION 14 CAREFULLY
							TO UNDERSTAND YOUR RIGHTS AND OBLIGATIONS WITH
							RESPECT TO THE RESOLUTION OF ANY CLAIM.
						</Paragraph>
					</Group>
					<Group>
						<Paragraph weight="bold">
							2. RIGHTS AND RESTRICTIONS
						</Paragraph>
						<Paragraph>
							a. Right to Access and Use the Services. Subject to
							and conditioned on your compliance with this TOS,
							Selfstorage.com hereby grants you a personal,
							non-exclusive, non-transferable, non-sublicensable,
							revocable license solely to use the Services only
							for your personal, non-commercial use to initiate
							and request Reservations for Units. Your access to
							and use of the Services must further comply in all
							material respects with all usage guidelines we post.
							All of the foregoing terms and guidelines may be
							updated periodically, and your use (or continued
							use) of Selfstorage.com constitutes your agreement
							to abide by them.
						</Paragraph>

						<Paragraph>
							b. In addition to this TOS, our{' '}
							<Link href="https://www.selfstorage.com/legal/privacy">
								Privacy Policy
							</Link>
							(“Privacy Policy”) applies to how Selfstorage.com
							may process information provided as part of the
							Services. You acknowledge and agree that by
							accessing or using the Services, Selfstorage.com may
							receive certain information about you, including
							personal data, as set forth in the Privacy Policy,
							and Selfstorage.com may collect, use, disclose,
							store, share, and process such personal data in
							accordance with such Privacy Policy.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							3. PROPRIETARY RIGHTS
						</Paragraph>
						<Paragraph>
							a. You grant Selfstorage.com and its service
							providers a perpetual, irrevocable, worldwide,
							royalty-free, fully-paid-up, non-exclusive,
							sublicensable, transferable license to use,
							reproduce, modify, adapt, create derivative works
							from, publicly perform, publicly display,
							distribute, make and have made all content (in any
							form and any medium, whether now known or later
							developed) that you provide in connection with the
							Services. You acknowledge and agree that the
							technical processing and transmission of data
							associated with the Services, may require: (i)
							transmissions over various networks and across
							borders; and (ii) modifications to conform, connect,
							and adapt to technical requirements of networks or
							devices.
						</Paragraph>
						<Paragraph>
							b. The Services provided to you hereunder or
							available to you through the Services are licensed,
							not sold, and Selfstorage.com retains and reserves
							all rights not expressly granted in this TOS. You
							acknowledge and agree that, as between you and
							Selfstorage.com, Selfstorage.com and its licensors
							own all rights, title and interest (including all
							intellectual property rights) in the Services and
							all data, content, and other materials within the
							Services. The Services are protected by U.S. and
							international copyright and other intellectual
							property laws and treaties. Selfstorage.com reserves
							all rights not expressly granted to you in this TOS.
						</Paragraph>
						<Paragraph>
							c. You may not share your account or password with
							anyone. You are fully responsible for all activities
							that occur under your account. You agree to notify
							Selfstorage.com immediately of any unauthorized use
							of your account or password or any other similar
							breach of security. If your account remains inactive
							for three months or longer, Selfstorage.com reserves
							the right to suspend or terminate your account, with
							or without notice to you, and delete your content
							all without liability.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							4. RESERVATION TERMS
						</Paragraph>
						<Paragraph>
							a. Selfstorage.com reserves the right, in its sole
							discretion, to deny access to the Service and
							decline Reservations, at any time and for any
							reason.
						</Paragraph>
						<Paragraph>
							b. As a condition of your use of the Reservation
							Service, you warrant that: (i) you will use the
							Service in accordance with this TOS; (ii) you
							possess the legal authority to create a binding
							legal obligation; (iii) you will only use the
							Service to initiate legitimate Reservations for you
							or for another person or company for whom you are
							legally authorized to act; (iv) all information
							supplied by you via the Service is true, accurate,
							current and complete; and (v) you are at least 18
							years of age.
						</Paragraph>
						<Paragraph>
							c. If you use the Service to make a reservation
							online, certain automated dialing features of the
							Service may call you at the telephone number(s) that
							you provide to us including without limitation in
							order to try to connect you with the lessor of the
							Unit(s) that you have expressed interest in via the
							Service. The personnel with whom you speak as a
							result of any such call are the personnel of the
							third party lessors, not Selfstorage.com.
						</Paragraph>
						<Paragraph>
							d. Selfstorage.com is not involved in any lease
							transaction pertaining to a Unit, and any such
							transaction is solely between you and the lessor.
							Selfstorage.com has no control over and does not
							guarantee the quality, safety or legality of spaces
							advertised, the truth or accuracy of listings, the
							ability of lessors to lease the storage space, or
							that a lessor will actually complete a transaction.
						</Paragraph>
						<Paragraph>
							e. Selfstorage.com's role in the rental of storage
							spaces is limited to publishing advertising provided
							by lessors and communicating your requests for
							Reservations to storage operators. Selfstorage.com
							is not responsible for, and does not engage in,
							brokering, selling, purchasing, exchanging, or
							leasing posted properties. Selfstorage.com does not
							purport to broker transactions, even though
							Selfstorage.com may be licensed as a real estate
							broker in one or more states. Selfstorage.com does
							not counsel lessors or lessees, show properties,
							negotiate sales contracts, or hold a position of
							trust and confidence, whether or not in connection
							with the Services. No brokerage relationship or any
							agency or fiduciary relationship is intended to be
							or shall be deemed to have been created between
							Selfstorage.com and any lessee of a storage space.
							Selfstorage.com makes no representations about
							properties advertised on this Website, about the
							lessors of such properties, or about the accuracy of
							information provided by such lessors. Prospective
							lessees are advised to perform all due diligence
							before entering into a contract to rent or lease
							storage space.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">5. REVIEW TERMS</Paragraph>
						<Paragraph>
							a. In connection with the Services, you may submit
							reviews of facilities from which you rented Units,
							by survey or otherwise. You acknowledge
							Selfstorage.com's right to publish any reviews you
							submit. Selfstorage.com only posts reviews submitted
							by customers who have had a firsthand experience
							with the facility being reviewed. You may not submit
							a review if you have a relationship with the
							facility being reviewed, whether as an owner,
							manager, employee or competitor. You may not submit
							a review if you receive compensation or the promise
							of compensation (such as a discount off the
							facility's rental fees or a free product) in
							exchange for the review.
						</Paragraph>
						<Paragraph>
							b. Reviews submitted to Selfstorage.com and posted
							on the Website reflect the opinions and beliefs of
							the individual reviewers and not of Selfstorage.com
							or any of its affiliates, officers, directors or
							employees. Selfstorage.com does not endorse any of
							the opinions expressed by reviewers. Selfstorage.com
							is not affiliated with any facility listed or
							reviewed on the Website.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							6. USER CONDUCT AND RESTRICTIONS
						</Paragraph>
						<Paragraph>
							a. In your use of the Services, you will not:
						</Paragraph>
						<Paragraph>
							i. use, reproduce, modify, adapt, create derivative
							works from, sublicense, publicly perform, publicly
							display, distribute, sell, lease, rent, make, have
							made, assign, pledge, transfer or otherwise grant
							rights to the Services, except as expressly
							permitted under this TOS;
						</Paragraph>
						<Paragraph>
							ii. reverse engineer, disassemble, decompile,
							translate, or otherwise attempt to derive trade
							secrets, algorithms, or the source code,
							architectural framework, or data records, within or
							associated with the Services;
						</Paragraph>
						<Paragraph>
							iii. interfere with or disrupt the integrity or
							performance of the Services, including by disrupting
							the ability of any other person to use or enjoy the
							Services;
						</Paragraph>
						<Paragraph>
							iv. provide use of the Services on a service bureau,
							rental or managed services basis, provide or permit
							other individuals or entities to create Internet
							"links" to the Services or "frame" or "mirror" the
							Services on any other server, or wireless or
							Internet-based device;
						</Paragraph>
						<Paragraph>
							v. access the Services for the purpose of
							developing, marketing, selling or distributing any
							product or service that competes with or includes
							features substantially similar to the Services (or
							assisting any third party in doing so);
						</Paragraph>
						<Paragraph>
							vi. violate any applicable local, state, provincial,
							federal or international law or regulation, or use
							the Services for any illegal, unauthorized or
							otherwise improper purposes, including to store or
							transmit malicious code, or to store or transmit
							material in violation of third-party privacy rights;
						</Paragraph>
						<Paragraph>
							vii. remove or obscure any proprietary notice that
							appears within the Services;
						</Paragraph>
						<Paragraph>
							viii. impersonate any person or entity, including
							Selfstorage.com personnel, or falsely state or
							otherwise misrepresent your affiliation with
							Selfstorage.com, or any other entity or person;
						</Paragraph>
						<Paragraph>
							ix. forge headers or otherwise manipulate
							identifiers in order to disguise the origin of any
							content transmitted through the Services;
						</Paragraph>
						<Paragraph>
							x. take any action that imposes an unreasonable or
							disproportionately heavy load on the Services or its
							infrastructure; or
						</Paragraph>
						<Paragraph>
							xi. use spiders, crawlers, robots, scrapers,
							automated tools or any other automated or other any
							means not explicitly authorized by Selfstorage.com
							to access the Services; or download, reproduce, or
							archive any substantial portion of the Services, or
							crawl, monitor, copy, or "scrape" web pages or the
							content contained.
						</Paragraph>
						<Paragraph>
							xii. share username and password credentials with
							any employee, agent, or representative(s) of a
							third-party.
						</Paragraph>
						<Paragraph>
							b. You will not: upload, post, email, store,
							transmit, or otherwise make available any content
							that:
						</Paragraph>
						<Paragraph>
							i. is illegal, harmful, threatening, abusive,
							harassing, tortious, defamatory, vulgar, obscene,
							libelous, invasive of another's privacy, hateful, or
							otherwise objectionable;
						</Paragraph>
						<Paragraph>
							ii. may not be made available under any law or under
							contractual or fiduciary relationships (such as
							confidential or proprietary information learned as
							part of an employment relationship or under a
							non-disclosure agreement);
						</Paragraph>
						<Paragraph>
							iii. infringes any patent, trademark, trade secret,
							copyright, or other proprietary right of any party;
						</Paragraph>
						<Paragraph>
							iv. consists of unsolicited or unauthorized
							advertising, promotional materials, junk mail, spam,
							chain letters, pyramid schemes, commercial
							electronic messages, or any other form of
							solicitation;
						</Paragraph>
						<Paragraph>
							v. contains software viruses or any other code,
							files or programs designed to interrupt, destroy or
							limit the functionality of any software or hardware;
						</Paragraph>
						<Paragraph>
							vi. contains infringing, libelous, or otherwise
							unlawful or tortious material; or
						</Paragraph>
						<Paragraph>
							vii. consists of information that you know or have
							reason to know is false or inaccurate.
						</Paragraph>
						<Paragraph>
							c. Selfstorage.com’s failure to enforce any of these
							restrictions or guidelines shall not act as a waiver
							for any future enforcement, will not be considered a
							breach of this TOS by Selfstorage.com, and does not
							create a private right of action for any other
							party.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							7. LINKS TO THIRD-PARTY SITES
						</Paragraph>
						<Paragraph>
							The Services may include or provide access to third
							party products, services, content, or offerings,
							including advertising for such (“Third-Party
							Services”). You acknowledge that different terms of
							service and privacy policies may apply to your use
							of such Third-Party Services and that terms and
							policies are solely between you and the advertiser
							or other third party. You agree that Selfstorage.com
							does not endorse and is not responsible or liable
							for any issues related to Third Party Services. Any
							maps provided on via the Services that are provided
							by Google are subject to the Google Maps terms and
							conditions.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							8. COPYRIGHT INFRINGEMENT
						</Paragraph>

						<Paragraph>
							a. If you believe that your work has been made
							available through the Services in a way that
							constitutes copyright infringement, whether in such
							comments, reviews, or otherwise please provide our
							Copyright Agent with the following information: (i)
							an electronic or physical signature of the person
							authorized to act on behalf of the owner of the
							copyright interest; (ii) a description of the
							copyrighted work that you claim has been infringed;
							(iii) a description of where the material that you
							claim is infringing is located on the Website; (iv)
							your address, telephone number, and primary email
							address; (v) a written statement by you that you
							have a good faith belief that the disputed use is
							not authorized by the copyright owner, its agent, or
							the law; and (vi) a statement by you, made under
							penalty of perjury, that the above information in
							your notice is accurate and that you are the
							copyright owner or authorized to act on the
							copyright owner's behalf.
						</Paragraph>

						<Paragraph>
							b. Send any notices of suspected copyright
							infringement issues to{' '}
							<Link href="mailto:legal@Selfstorage.com">
								legal@Selfstorage.com
							</Link>{' '}
							and reference Copyright in the subject line.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">9. INDEMNIFICATION</Paragraph>
						<Paragraph>
							a. You shall indemnify and hold Selfstorage.com and
							its affiliates, and each of their officers,
							directors, employees, agents, partners and licensors
							(collectively, “Selfstorage.com Parties”) harmless
							from and against all losses, damages, costs,
							liabilities, and expenses, including reasonable
							attorneys’ fees, to extent resulting from or arising
							out of any third party claim, demand, or action due
							to (a) content you provide to Selfstorage.com; (b)
							your violation of this TOS, any law or regulation,
							or any rights (including intellectual property
							rights) of another party; or (c) your use of the
							Services, except as expressly permitted in this TOS.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">10. DISCLAIMERS</Paragraph>
						<Paragraph>
							a. You shall indemnify and hold Selfstorage.com and
							its affiliates, and each of their officers,
							directors, employees, agents, partners and licensors
							(collectively, “Selfstorage.com Parties”) harmless
							from and against all losses, damages, costs,
							liabilities, and expenses, including reasonable
							attorneys’ fees, to extent resulting from or arising
							out of any third party claim, demand, or action due
							to (a) content you provide to Selfstorage.com; (b)
							your violation of this TOS, any law or regulation,
							or any rights (including intellectual property
							rights) of another party; or (c) your use of the
							Services, except as expressly permitted in this TOS.
						</Paragraph>
						<Paragraph>
							b. SELFSTORAGE.COM MAKES NO WARRANTY OR
							REPRESENTATION THAT: (i) THE SERVICES WILL MEET YOUR
							REQUIREMENTS; (ii) ACCESS TO THE SERVICES WILL BE
							UNINTERRUPTED, TIMELY, SECURE, VIRUS-FREE, OR
							ERROR-FREE; OR (iii) THE INFORMATION AND ANY RESULTS
							THAT MAY BE OBTAINED FROM ACCESS TO OR USE OF THE
							SERVICES WILL BE ACCURATE, RELIABLE, CURRENT, OR
							COMPLETE.
						</Paragraph>
						<Paragraph>
							c. ALL CONTENT MADE AVAILABLE THROUGH THE SERVICES
							IS MADE AVAILABLE FOR INFORMATIONAL PURPOSES ONLY.
							YOU ARE SOLELY RESPONSIBLE FOR CONFIRMING THE
							ACCURACY OF ALL CONTENT BEFORE TAKING OR OMITTING
							ANY ACTION.
						</Paragraph>
						<Paragraph>
							d. ALL CONTENT MADE AVAILABLE THROUGH THE SERVICES
							IS MADE AVAILABLE FOR INFORMATIONAL PURPOSES ONLY.
							YOU ARE SOLELY RESPONSIBLE FOR CONFIRMING THE
							ACCURACY OF ALL CONTENT BEFORE TAKING OR OMITTING
							ANY ACTION.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							11. LIMITATION OF LIABILITY
						</Paragraph>
						<Paragraph>
							a. SELFSTORAGE.COM PARTIES SHALL NOT BE LIABLE FOR
							ANY LOST PROFITS, LOSS OF DATA OR GOODWILL, OR COST
							OF COVER, OR FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
							EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES,
							INCLUDING DAMAGES ARISING FROM ANY TYPE OR MANNER OF
							COMMERCIAL, BUSINESS, OR FINANCIAL LOSS, EVEN IF
							SELFSTORAGE.COM PARTIES HAD ACTUAL OR CONSTRUCTIVE
							KNOWLEDGE OF THE POSSIBILITY OF SUCH DAMAGES AND
							REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE.
							IN NO EVENT SHALL SELFSTORAGE.COM PARTIES’ TOTAL
							LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING FROM
							OR RELATING TO THIS TOS OR YOUR ACCESS TO OR USE OF
							(OR INABILITY TO ACCESS OR USE) THE SERVICES OR
							CONTENT EXCEED THE AMOUNT PAID BY YOU TO
							SELFSTORAGE.COM FOR ACCESS TO THE SERVICES WITHIN
							THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE
							DATE ON WHICH THE APPLICABLE CLAIM(S) AROSE.
						</Paragraph>
						<Paragraph>
							b. THE FOREGOING LIMITATIONS, EXCLUSIONS AND
							DISCLAIMERS SHALL APPLY REGARDLESS OF WHETHER SUCH
							LIABILITY ARISES FROM ANY CLAIM BASED UPON CONTRACT,
							WARRANTY, TORT (INCLUDING NEGLIGENCE), STRICT
							LIABILITY OR OTHERWISE, AND WHETHER OR NOT THE PARTY
							HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH LOSS OR
							DAMAGE. INSOFAR AS APPLICABLE LAW PROHIBITS ANY
							LIMITATION ON LIABILITY HEREIN, THE PARTIES AGREE
							THAT SUCH LIMITATION WILL BE AUTOMATICALLY MODIFIED,
							BUT ONLY TO THE EXTENT SO AS TO MAKE THE LIMITATION
							COMPLIANT WITH APPLICABLE LAW. THE PARTIES AGREE
							THAT THE LIMITATIONS ON LIABILITIES SET FORTH HEREIN
							ARE AGREED ALLOCATIONS OF RISK AND SUCH LIMITATIONS
							WILL APPLY NOTWITHSTANDING THE FAILURE OF ESSENTIAL
							PURPOSE OF ANY LIMITED REMEDY.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							12. SUSPENSION AND TERMINATION
						</Paragraph>
						<Paragraph>
							a. If you violate this TOS, Selfstorage.com may,
							with or without notice to you, immediately suspend
							or terminate your access and use of the Services.
						</Paragraph>
						<Paragraph>
							b. Selfstorage.com reserves the right at any time to
							modify, suspend, or discontinue the Services (or any
							portion thereof) with or without notice, and
							Selfstorage.com shall not be liable to you or any
							third party for any such modification or
							discontinuance;
						</Paragraph>
						<Paragraph>
							c. Upon termination of this TOS for any reason: (i)
							Selfstorage.com, in its sole discretion, may remove
							and discard your content and information; (ii) you
							will immediate cease your use of the Services; and
							(iii) any provision that, by its terms, is intended
							to survive the expiration or termination of this TOS
							shall survive such expiration or termination.
							Further, you agree that Selfstorage.com shall not be
							liable to you or any third party for any termination
							of your account or access to the Services.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">13. GOVERNING LAW</Paragraph>

						<Paragraph>
							This TOS shall be governed by and construed and
							enforced in accordance with the United States
							Federal Arbitration Act, other applicable federal
							laws and the laws of the State of Texas, without
							regard to conflict of laws principles. The parties
							agree that neither the United Nations Convention on
							Contracts for the International Sale of Goods, nor
							the Uniform Computer Information Transaction Act
							(UCITA) shall apply to this TOS, regardless of the
							states in which the parties do business or are
							incorporated.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							14. BINDING ARBITRATION AND CLASS ACTION WAIVER
						</Paragraph>
						<Paragraph>
							a. ALL CLAIMS ARISING IN CONNECTION WITH THIS
							AGREEMENT SHALL BE RESOLVED BY FINAL AND BINDING
							ARBITRATION RATHER THAN IN COURT, EXCEPT THAT YOU
							MAY ASSERT CLAIMS IN SMALL CLAIMS COURT (DEFINED FOR
							THE PURPOSES OF THIS TOS AS A COURT OF LIMITED
							JURISDICTION THAT MAY ONLY HEAR CLAIMS NOT EXCEEDING
							$5,000) IF YOUR CLAIMS ARE WITHIN THE COURT’S
							JURISDICTION. THERE IS NO JUDGE OR JURY IN
							ARBITRATION, AND COURT REVIEW OF AN ARBITRATION
							AWARD IS LIMITED.
						</Paragraph>
						<Paragraph>
							b. The arbitration shall be conducted by the
							American Arbitration Association (AAA) under its
							then-applicable Commercial Arbitration Rules or, as
							appropriate, its Consumer Arbitration Rules. The
							AAA’s rules are available at http://www.adr.org/.
							Payment of all filing, administration and arbitrator
							fees shall be governed by the AAA’s rules. The
							arbitration shall be conducted in the English
							language by a single independent and neutral
							arbitrator. For any hearing conducted in person as
							part of the arbitration, you agree that such hearing
							shall be conducted in Austin, Texas or, if the
							Consumer Arbitration Rules apply, another location
							reasonably convenient to both parties with due
							consideration of their ability to travel and other
							pertinent circumstances, as determined by the
							arbitrator. The decision of the arbitrator shall be
							final and binding. Judgment on the arbitral award
							may be entered in any court of competent
							jurisdiction.
						</Paragraph>
						<Paragraph>
							c. WE EACH AGREE THAT ALL CLAIMS SHALL BE RESOLVED
							ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS,
							CONSOLIDATED OR REPRESENTATIVE ACTION OR OTHER
							SIMILAR PROCESS (INCLUDING ARBITRATION). IF FOR ANY
							REASON A CLAIM PROCEEDS IN COURT RATHER THAN IN
							ARBITRATION, WE EACH WAIVE ANY RIGHT TO A JURY TRIAL
							AND AGREE THAT SUCH CLAIM SHALL BE BROUGHT ONLY IN A
							COURT OF COMPETENT JURISDICTION IN AUSTIN, TEXAS.
							YOU HEREBY SUBMIT TO THE PERSONAL JURISDICTION AND
							VENUE OF SUCH COURTS AND WAIVE ANY OBJECTION ON THE
							GROUNDS OF VENUE, FORUM NON- CONVENIENS OR ANY
							SIMILAR GROUNDS WITH RESPECT TO ANY SUCH CLAIM.
						</Paragraph>

						<Paragraph>
							d. Notwithstanding anything to the contrary, you and
							Selfstorage.com may seek injunctive relief and any
							other equitable remedies from any court of competent
							jurisdiction to protect its intellectual property
							rights, whether in aid of, pending, or independently
							of the resolution of any dispute pursuant to the
							arbitration procedures set forth in this Section 14.
						</Paragraph>

						<Paragraph>
							e. If Selfstorage.com implements any material change
							to this Section 14, such change shall not apply to
							any Claim for which you provided written notice to
							Selfstorage.com before the implementation of the
							change.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							15. LEGAL COMPLIANCE
						</Paragraph>
						<Paragraph>
							You represent and warrant that you will comply with
							all applicable foreign, federal, state, and local
							laws, rules and regulations, including without
							limitation, U.S. export laws and import and use laws
							of the country where Licensed Material is delivered
							or used and you are not: (a) located in a country
							that is subject to a U.S. Government embargo, or
							designated by the U.S. Government as a “terrorist
							supporting” country; and (b) listed on any U.S.
							Government list of prohibited or restricted parties,
							including the Specially Designated Nationals List.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							16. U.S. GOVERNMENT ENTITIES
						</Paragraph>
						<Paragraph>
							This section applies to access to or use of the
							Services by a branch or agency of the United States
							Government. The Services includes “commercial
							computer software” and “commercial computer software
							documentation” as such terms are used in 48 C.F.R.
							12.212 and qualifies as “commercial items” as
							defined in 48 C.F.R. 2.101. Such items are provided
							to the United States Government: (a) for acquisition
							by or on behalf of civilian agencies, consistent
							with the policy set forth in 48 C.F.R. 12.212; or
							(b) for acquisition by or on behalf of units of the
							Department of Defense, consistent with the policies
							set forth in 48 C.F.R. 227.7202-1 and 227.7202-3.
							The United States Government shall acquire only
							those rights set forth in this TOS with respect to
							the such items, and any access to or use of the
							Services by the United States Government
							constitutes: (i) agreement by the United States
							Government that that such items are “commercial
							computer software” and “commercial computer software
							documentation” as defined in this section; and (ii)
							acceptance of the rights and obligations herein.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							17. CALIFORNIA USERS & RESIDENTS
						</Paragraph>
						<Paragraph>
							In accordance with California Civil Code §1789.3,
							you may report complaints to the Complaint
							Assistance Unit of the Division of Consumer Services
							of the California Department of Consumer Affairs by
							contacting such unit in writing at 1625 North Market
							Blvd., Suite N 112, Sacramento, CA 95834, or by
							telephone at (800) 952- 5210.
						</Paragraph>
					</Group>

					<Group>
						<Paragraph weight="bold">
							18. GENERAL PROVISIONS
						</Paragraph>

						<Paragraph>
							This TOS constitutes the entire agreement between
							you and Selfstorage.com concerning your access to
							and use of the Services. It supersedes all prior and
							contemporaneous oral or written negotiations and
							agreements between you and Selfstorage.com with
							respect to such subject matter. In the event of any
							conflict between or among this TOS and any end user
							license agreement, privacy policy or usage
							guidelines to which this TOS refers, the terms and
							conditions of this TOS shall take precedence and
							govern. This TOS may not be amended by you except in
							a writing executed by you and an authorized
							representative of Selfstorage.com. Except as
							otherwise expressly provided in this TOS, there
							shall be no third-party beneficiaries to this TOS.
							For the purposes of this TOS, the words “such as,”
							“include,” “includes” and “including” shall be
							deemed to be followed by the words “without
							limitation.” You may not assign or delegate any
							right or obligation under this TOS without the prior
							written consent of Selfstorage.com. The failure of
							Selfstorage.com to exercise or enforce any right or
							provision of this TOS shall not constitute a waiver
							of such right or provision. If any provision of this
							TOS is held to be invalid or unenforceable under
							applicable law, then such provision shall be
							construed, limited, modified or, if necessary,
							severed to the extent necessary to eliminate its
							invalidity or unenforceability, without in any way
							affecting the remaining parts of this TOS. Any
							prevention of or delay in performance by
							Selfstorage.com hereunder due to labor disputes,
							acts of god, failure of the Internet, governmental
							restrictions, enemy or hostile governmental action,
							fire or other casualty or other causes beyond its
							reasonable control shall excuse the performance of
							its obligations for a period equal to the duration
							of any such prevention or delay.
						</Paragraph>
					</Group>
				</Container>
			</Section>
		</Page>
	);
}

export default LegalPage;
